import { ArrowRight, Check, GripVertical, Settings2 } from "lucide-react";
import { CT } from "@/lib/connectionsTheme";

export const CapabilityCard = ({
  capability, subStatuses, onSetup, highlighted, anchorId, cardRef,
  draggable, onDragStart, onDragOver, onDrop, onDragEnd, isDragging, dragDisabled,
}) => {
  const { key, label, subtitle, icon: Icon, iconBg, iconColor, disabled } = capability;

  const configuredCount = capability.subCapabilities.filter((s) => subStatuses[s.key]?.status && subStatuses[s.key].status !== "not_configured").length;
  const totalCount = capability.subCapabilities.length;
  const overallStatus = configuredCount === 0 ? "none" : configuredCount < totalCount ? "partial" : "active";
  const overallLabel = overallStatus === "none" ? "Not Configured" : overallStatus === "partial" ? "Partially Configured" : "Active";
  const overallBadge = overallStatus === "active" ? CT.badgeActive : overallStatus === "partial" ? CT.badgeAmber : CT.badgeMuted;

  if (disabled) {
    return (
      <div
        id={anchorId}
        ref={cardRef}
        draggable={!dragDisabled}
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDrop={onDrop}
        onDragEnd={onDragEnd}
        className={`${CT.card} relative flex flex-col items-center justify-center gap-2 p-5 text-center opacity-60 ${isDragging ? "opacity-30" : ""} ${dragDisabled ? "" : "cursor-grab active:cursor-grabbing"}`}
        data-testid={`org-connections-card-${key}`}
      >
        {!dragDisabled && (
          <span className="absolute right-2 top-2 text-slate-300 dark:text-slate-600" data-testid={`org-connections-drag-handle-${key}`}>
            <GripVertical size={14} />
          </span>
        )}
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${iconBg} grayscale`}>
          <Icon size={18} className={iconColor} strokeWidth={1.8} />
        </span>
        <p className="text-sm font-bold text-slate-500 dark:text-slate-300">{label}</p>
        <p className="line-clamp-2 text-xs text-slate-400 dark:text-slate-500">{subtitle}</p>
        <span className={`${CT.badge} ${CT.badgeMuted}`} data-testid={`org-connections-comingsoon-${key}`}>Coming Soon</span>
      </div>
    );
  }

  return (
    <div
      id={anchorId}
      ref={cardRef}
      draggable={!dragDisabled}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      className={`${CT.card} ${CT.cardHover} flex flex-col p-3 sm:p-4 ${isDragging ? "opacity-30" : ""} ${dragDisabled ? "" : "cursor-grab active:cursor-grabbing"} ${highlighted ? "border-blue-400/60 shadow-[0_0_20px_rgba(59,130,246,0.15)] dark:shadow-[0_0_20px_rgba(59,130,246,0.3)]" : ""}`}
      data-testid={`org-connections-card-${key}`}
    >
      <div className="flex items-start justify-between gap-2">
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${iconBg}`}>
          <Icon size={16} className={iconColor} strokeWidth={1.8} />
        </span>
        <div className="flex items-center gap-1.5">
          <span className={`${CT.badge} ${overallBadge} shrink-0`} data-testid={`org-connections-overall-status-${key}`}>{overallLabel}</span>
          {!dragDisabled && (
            <span className="text-slate-300 dark:text-slate-600" data-testid={`org-connections-drag-handle-${key}`}>
              <GripVertical size={14} />
            </span>
          )}
        </div>
      </div>

      <p className="mt-2 line-clamp-1 text-base font-bold text-slate-900 dark:text-white">{label}</p>
      <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{subtitle}</p>

      <div className="mt-3 flex flex-wrap items-start gap-x-3 gap-y-1.5 border-t border-slate-100 pt-3 dark:border-white/5">
        {capability.subCapabilities.map((s) => {
          const configured = subStatuses[s.key]?.status && subStatuses[s.key].status !== "not_configured";
          return (
            <span key={s.key} className={`inline-flex items-center gap-1 text-[11px] font-medium ${configured ? "text-slate-600 dark:text-slate-300" : "text-slate-400 dark:text-slate-500"}`} data-testid={`org-connections-checklist-${key}-${s.key}`}>
              <Check size={11} className={configured ? "text-emerald-500 dark:text-emerald-400" : "text-slate-300 dark:text-slate-600"} /> {s.label}
            </span>
          );
        })}
      </div>

      <div className="mt-4 flex items-center gap-2">
        <button
          type="button"
          onClick={() => onSetup(key)}
          data-testid={`org-connections-setup-${key}`}
          className={`${CT.btnPrimary} flex-1`}
        >
          {overallStatus === "none" ? "Set Up" : overallStatus === "partial" ? "Continue Setup" : "Manage"} <ArrowRight size={12} />
        </button>
        <button type="button" onClick={() => onSetup(key)} data-testid={`org-connections-gear-${key}`} className={CT.btnIcon} aria-label={`${label} settings`}>
          <Settings2 size={14} />
        </button>
      </div>
    </div>
  );
};
